import React, { useState, useEffect, useRef } from 'react';
import { SdfButton, SdfCard, SdfAlert, SdfIcon, SdfBusyIndicator } from '@synerg/react-components';
import get from 'lodash/get';
import axios from 'axios';
import { withNotificationContext } from '../../shared/notifications';
import { setLoading, updateNotificationSettings, useApplicationState } from '../../../utilities/applicationState';
import { withConfig } from '../../../utilities/appConfig';

const NotificationIcon = ({
	showNotificationMenu,
	setShowNotificationMenu,
	config
}) => {
	const [errorMsg, setErrorMsg] = useState('');
	const [{ notificationSettings, loading }, dispatch] = useApplicationState();
	const { certificateExpiration = true, webhookFailure = true } = notificationSettings;
	const [expirationToggle, setExpirationToggle] = useState(certificateExpiration);
	const [webhookToggle, setWebhookToggle] = useState(webhookFailure);
	const menuRef = useRef(null);
	const notificationsIconRef = useRef(null);

	const onClickOutside = e => {
		const element = e.target;
		if (notificationsIconRef.current && notificationsIconRef.current.contains(element)) {
			return;
		}
		if (menuRef.current && !menuRef.current.contains(element)) {
			return setShowNotificationMenu(false);
		}
	};

	// Save user settings
	const saveAllSettings = async () => {
		const newSettings = {
			...notificationSettings,
			certificateExpiration: expirationToggle,
			webhookFailure: webhookToggle
		};
		
		dispatch(setLoading(true));
		try {
			const { data } = await axios.post(`${import.meta.env.VITE_ROOT_PATH}/api/notification-settings`, newSettings);
			dispatch(updateNotificationSettings(data));
			setShowNotificationMenu(false);
			errorMsg && setErrorMsg('');
		} catch (e) {
			const errorMessage = get(e, 'response.data.message', 'Something went wrong while updating notifications settings.');
			setErrorMsg(errorMessage);
		}
		dispatch(setLoading(false));
	};

	// fetch user settings when component mounts
	useEffect(() => {
		const fetchNotificationSettings = async () => {
			try {
				const response = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/notification-settings`);
				dispatch(updateNotificationSettings(response.data));
			} catch (error) {
				console.warn('Failed to fetch notification settings:', error);
				// fall back to defaults if fetch fails
			}
		};

		fetchNotificationSettings();
	}, [dispatch]);

	// use the fetched settings
	useEffect(() => {
		setExpirationToggle(certificateExpiration);
		setWebhookToggle(webhookFailure);
	}, [certificateExpiration, webhookFailure, showNotificationMenu]);

	useEffect(() => {
		document.body.addEventListener('click', onClickOutside);
		return () => document.removeEventListener('click', onClickOutside);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<div ref={notificationsIconRef} className="mr-6 cursor-pointer"
				onClick={() => { 
					setShowNotificationMenu(!showNotificationMenu); 
					setExpirationToggle(certificateExpiration);
					setWebhookToggle(webhookFailure);
				}}>
				<sdf-icon icon="alert-notification"></sdf-icon>
			</div>

			<div ref={menuRef} className={showNotificationMenu ? '' : 'hidden'}
				style={{ position: 'fixed', top: '4rem', right: '8rem' }}
			>
				<SdfCard>
					<div className="pl-inline-md pt-3 pb-4">
						<div className='text-default font-medium font-bold mb-2'>Notification Settings</div>
						<div className='text-default text-sm mb-10'>
							These settings are only for you and will
							<br /> not impact your teammates' settings.
						</div>
						<div>
							{/* Certificate Expiration Toggle */}
							<div className='w-64 flex text-default font-medium text-lg font-extrabold mb-4'>
								<div className="w-48">Signed certificate
									<br />expiration
								</div>
								<div className="pl-inline-lg flex justify-between items-center">
									<sdf-switch slot="action" checked={expirationToggle}
										onClick={(ev) => {
											!notificationSettings.error && setExpirationToggle(!expirationToggle);
										}}
										disabled={notificationSettings.error}
									>
										{<SdfIcon
											icon={expirationToggle ? "action-confirm" : 'action-close'}
											style={{
												position: 'absolute',
												fontSize: '12px', ...(expirationToggle ? { right: '12px' } : { left: '4px' })
											}}
										>
										</SdfIcon>}
									</sdf-switch>
								</div>
							</div>
							<div className="text-tertiary text-sm mb-10">
								When your signed certificate is within 60
								<br />days of expiration, you will receive
								<br />multiple notifications at regular intervals
								<br />until it has expired.
							</div>
							{/* Webhook Failure Toggle */}
							{config?.['webhookfeatureflag']?.enabled && (
								<>
									<div className='w-64 flex text-default font-medium text-lg font-extrabold mb-4'>
										<div className="w-48">Webhook Failure Alerts</div>
										<div className="pl-inline-lg flex justify-between items-center">
											<sdf-switch slot="action" checked={webhookToggle}
												onClick={(ev) => {
													!notificationSettings.error && setWebhookToggle(!webhookToggle);
												}}
												disabled={notificationSettings.error}
											>
												{<SdfIcon
													icon={webhookToggle ? "action-confirm" : 'action-close'}
													style={{
														position: 'absolute',
														fontSize: '12px', ...(webhookToggle ? { right: '12px' } : { left: '4px' })
													}}
												>
												</SdfIcon>}
											</sdf-switch>
										</div>
									</div>
									<div className="text-tertiary text-sm mb-10">
										When a webhook fails to deliver, you will be
										<br />notified by email.
									</div>
								</>
							)}
							<div className='flex gap-2 justify-end'>
								<SdfButton emphasis="secondary" onClick={() => {
									setShowNotificationMenu(false);
								}}>
									Cancel
								</SdfButton>
								<SdfButton emphasis="primary" onClick={saveAllSettings}
									{...(loading || notificationSettings.error) && { disabled: true }}
								>
									<div style={{ height: '1.625rem' }}>
										Save
										{loading && <SdfBusyIndicator className='relative' style={{ bottom: '1.25rem' }} size='sm' />}
									</div>
								</SdfButton>
							</div>
							{(notificationSettings.error || errorMsg) && <div className="pt-3">
								<SdfAlert className='w-68' type='inline' status='error'>{notificationSettings.error || errorMsg}</SdfAlert>
							</div>}
						</div>
					</div>
				</SdfCard>
			</div>
		</>
	);
}

export default withNotificationContext(withConfig(NotificationIcon));
